"use client"

import { React, useState, useEffect, useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Textarea } from "../ui/textarea"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { getApiBaseUrl } from "../../config/apiBase"

const GenericRefinementDiscussion = ({ type, itemId }) => {
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const { getAccessTokenSilently } = useAuth0()

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE } })
      const response = await fetch(`${getApiBaseUrl()}/api/refinement/${type}/${itemId}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch comments")
      }
      const data = await response.json()
      setComments(data)
    } catch (error) {
      console.error("Error fetching comments:", error)
      setError("Discussion could not be loaded.")
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently, itemId, type])

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return

    try {
      setError("")
      const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE } })
      const response = await fetch(`${getApiBaseUrl()}/api/refinement/${type}/${itemId}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      })

      if (!response.ok) {
        throw new Error("Failed to post comment")
      }

      setNewComment("")
      fetchComments()
    } catch (error) {
      console.error("Error posting comment:", error)
      setError("Your comment could not be posted. Please try again.")
    }
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Discussion</CardTitle>
        <p className="text-sm text-gray-500">Use comments to ask questions or build on a suggestion without changing the draft itself.</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
          {error ? (
            <p role="alert" className="text-red-500">{error}</p>
          ) : loading ? (
            <p>Loading comments...</p>
          ) : comments.length === 0 ? (
            <p className="text-gray-500">No comments yet. Start the discussion!</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-gray-50">
                <Avatar>
                  <AvatarImage src={comment.user?.avatarUrl || "/placeholder.svg"} />
                  <AvatarFallback>{comment.user?.name?.substring(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">{comment.user?.name}</span>
                    <span className="text-xs text-gray-500">{new Date(comment.createdAt).toLocaleString()}</span>
                  </div>
                  <p className="text-sm">{comment.content}</p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="space-y-2">
          <Textarea
            placeholder="Add to the discussion..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="min-h-[100px]"
          />
          <div className="flex justify-end">
            <Button onClick={handleSubmitComment}>Post Comment</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default GenericRefinementDiscussion
